// Auth provider oparty na sesji ciasteczkowej (spec.md rozdz. 9.2).
//
// Żadnego tokenu w localStorage: sesja siedzi w ciasteczku httpOnly i w Redis
// po stronie backendu, więc da się ją unieważnić natychmiast, a skrypt na
// stronie nie ma jak jej odczytać.

import type { AuthProvider, HttpError } from "@refinedev/core";

import { request } from "@/lib/http";

export interface AdminImpersonation {
  user_id: string;
  user_name: string;
  user_email: string;
  reason: string;
  started_at: string;
  expires_at: string;
  app_url: string;
}

export interface AdminIdentity {
  id: string;
  email: string;
  name: string;
  role: string;
  content_access: boolean;
  permissions: string[];
  /** Niepuste znaczy, że ten admin ma otwartą sesję rodziny (spec.md rozdz. 8.3). */
  impersonating: AdminImpersonation | null;
}

export interface LoginValues {
  email: string;
  password: string;
  totp_code?: string;
}

export const LOGIN_PATH = "/logowanie";

function asHttpError(error: unknown): HttpError {
  const problem = error as Partial<HttpError>;
  return {
    message: problem.message ?? "Nie udało się połączyć z serwerem.",
    statusCode: problem.statusCode ?? 0,
  };
}

export const authProvider: AuthProvider = {
  login: async (values: LoginValues) => {
    try {
      await request<AdminIdentity>("/auth/login", { method: "POST", body: values });
      return { success: true, redirectTo: "/" };
    } catch (error) {
      const problem = asHttpError(error);
      return {
        success: false,
        error: { name: "Logowanie nieudane", message: problem.message },
      };
    }
  },

  logout: async () => {
    try {
      await request<void>("/auth/logout", { method: "POST" });
    } catch {
      // Wygasła sesja też jest wylogowaniem - nie zatrzymujemy człowieka
      // na ekranie panelu tylko dlatego, że serwer odpowiedział błędem.
    }
    return { success: true, redirectTo: LOGIN_PATH };
  },

  check: async () => {
    try {
      await request<AdminIdentity>("/auth/me");
      return { authenticated: true };
    } catch {
      return { authenticated: false, logout: true, redirectTo: LOGIN_PATH };
    }
  },

  onError: async (error) => {
    const problem = asHttpError(error);
    if (problem.statusCode === 401) {
      return { logout: true, redirectTo: LOGIN_PATH, error: problem };
    }
    // 403 zostawiamy na ekranie: to nie jest powód do wylogowania, tylko
    // informacja, że ta rola nie ma tu czego szukać.
    return {};
  },

  getIdentity: async () => {
    try {
      return await request<AdminIdentity>("/auth/me");
    } catch {
      return null;
    }
  },

  getPermissions: async () => {
    try {
      const identity = await request<AdminIdentity>("/auth/me");
      return identity.permissions;
    } catch {
      return [];
    }
  },
};
